import React, { useState } from "react";
import Tooltip from "@mui/material/Tooltip";
import { DesktopIcon } from "../../../../assets/DesktopIcon";
import { MobileIcon } from "../../../../assets/MobileIcon";
import { EyeIcon } from "../../../../assets/EyeIcon";
import { ImageUploader } from "../SlideListView/ImageUploader";
import ImageExample from "../../../../assets/ImageExample.png";
import SliderInput from '../../components/SliderInput';
import { ExampleModal } from "./CarouselExampleModal";

interface SlideImageSectionProps {
    desktopSrc: string;
    mobileSrc: string;
    imageUrl: string;
    imageUrlEnabled: boolean;
    imageBrightness: number;
    showButton: boolean;
    isRestricted: boolean;
    isUploadingDesktop: boolean;
    isUploadingMobile: boolean;
    onImageUpload: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onFieldChange: (id: string, value: string | boolean | number) => void;
}

export const CarouselSlideEditorImageSection: React.FC<SlideImageSectionProps> = ({
    desktopSrc,
    mobileSrc,
    imageUrl,
    imageUrlEnabled,
    imageBrightness,
    showButton,
    isRestricted,
    isUploadingDesktop,
    isUploadingMobile,
    onImageUpload,
    onFieldChange,
}) => {
    const [showExample, setShowExample] = useState(false);

    const isImageUrlDisabled = isRestricted || showButton;
    const imageUrlDisabledReason = isRestricted
        ? "You don't have permission to edit this section."
        : showButton
            ? "Disable the button to use the image URL."
            : "";

    return (
        <section className="editSection editSection--image">
            <div className="editSection__head">
                <div className="title">Image</div>
                <button type="button" className="seeExample" onClick={() => setShowExample(true)}>
                    <span className="eyeIcon" />See Example <EyeIcon />
                </button>
            </div>

            <div className="imageGrid">
                <div className="desktopBlock">
                    <div className="blockLabel">
                        <DesktopIcon className="icon" /> Desktop
                        <span>1920x620 px · PNG or JPG for web</span>
                    </div>
                    <div className="uploaderRow">
                        <ImageUploader
                            fieldId="src"
                            previewSrc={desktopSrc}
                            isUploading={isUploadingDesktop}
                            disabled={isRestricted}
                            onUpload={onImageUpload}
                            onRemove={() => onFieldChange("src", "")}
                            useBackgroundPreview
                        />
                    </div>
                </div>

                <div className={`mobileBlock ${isRestricted ? "disabled" : ""}`}>
                    <div className="blockLabel">
                        <MobileIcon className="icon" /> Mobile
                        <span>1024x1024 px · PNG or JPG for web</span>
                    </div>
                    <div className="uploaderRow">
                        <ImageUploader
                            fieldId="mobile"
                            previewSrc={mobileSrc}
                            isUploading={isUploadingMobile}
                            disabled={isRestricted}
                            placeholderText="Upload Mobile Image"
                            onUpload={onImageUpload}
                            onRemove={() => onFieldChange("mobile", "")}
                            useBackgroundPreview
                        />
                    </div>
                </div>
            </div>

            <div className="slider-container-inline" style={{ marginTop: '20px' }}>
                <SliderInput
                    label="Image Brightness"
                    value={imageBrightness}
                    min={0}
                    max={100}
                    unit="%"
                    onChange={(val) => onFieldChange("imageBrightness", val)}
                />
            </div>

            <div className="imageUrl">
                <Tooltip
                    title={isImageUrlDisabled ? imageUrlDisabledReason : ""}
                    disableHoverListener={!isImageUrlDisabled}
                    arrow
                >
                    <div className="imageUrlRow">
                        <div className={showButton ? "imageUrlField disabled" : "imageUrlField"}>
                            <span style={{ display: "block" }}>
                                <input
                                    id="imageUrl"
                                    type="text"
                                    value={imageUrl !== "#" ? imageUrl : ""}
                                    onChange={(e) => onFieldChange("imageUrl", e.target.value)}
                                    className="input-round form-control"
                                    placeholder="Add URL for image: Only used if the button is disabled."
                                    disabled={isImageUrlDisabled}
                                />
                            </span>
                        </div>
                        <div className="imageUrlSwitch">
                            <p><b>Enable</b></p>
                            <span>
                                <input
                                    id="imageUrlEnabled"
                                    type="checkbox"
                                    checked={!!imageUrlEnabled}
                                    onChange={(e) => onFieldChange("imageUrlEnabled", e.target.checked)}
                                    disabled={isImageUrlDisabled}
                                />
                            </span>
                        </div>
                    </div>
                </Tooltip>
            </div>

            <ExampleModal
                title="Image example"
                imageSrc={ImageExample}
                open={showExample}
                onClose={() => setShowExample(false)}
            />
        </section>
    );
};
